import React from 'react';

export interface IFrontendContext {
    methods: {
        setShowForgotPassword: (value: boolean) => void;
        setShowCaptcha: (value: boolean) => void;
        setLookupOngoing: (value: boolean) => void;
        setLoginOngoing: (value: boolean) => void;
        setSignupOngoing: (value: boolean) => void;
        setSignupSecondStep: (value: boolean) => void;
        setShowModal: (value: MODAL_TYPES) => void;
        setIsModalLoading: (value: boolean) => void;
        setShowToSDialog: (value: boolean) => void;
        setIsToSLoading: (value: boolean) => void;
        setPasswordlessCreateAccountFlow: (value: boolean) => void;
        setSoftAccountEmailStatus: (value: string) => void;
        setPasswordlessEmailStatus: (value: string) => void;
        setVerificationEmailStatus: (value: string) => void;
        setResetPasswordEmailStatus: (value: string) => void;
    };
    state: {
        showForgotPassword: boolean;
        showCaptcha: boolean;
        lookupOngoing: boolean;
        loginOngoing: boolean;
        signupOngoing: boolean;
        signupSecondStep: boolean;
        showModal: MODAL_TYPES;
        isModalLoading: boolean;
        showToSDialog: boolean;
        isToSLoading: boolean;
        passwordlessCreateAccountFlow: boolean;
        softAccountEmailStatus: string;
        passwordlessEmailStatus: string;
        verificationEmailStatus: string;
        resetPasswordEmailStatus: string;
    };
}

export const useFrontendContext = () => {
    const value = React.useContext(FrontendContext);

    if (!value) {
        throw new Error(
            'useFrontendContext must be used within FrontendContext',
        );
    }

    return value;
};

export enum MODAL_TYPES {
    softAccountActivation = 'softAccountActivation',
    passwordless = 'passwordless',
    forgotPassword = 'forgotPassword',
    verification = 'verification',
    closed = 'closed',
}

const FrontendContext = React.createContext<any>(null);

const FrontendContextProvider = ({ children }: any) => {
    const [showForgotPassword, setShowForgotPassword] = React.useState<boolean>(
        false,
    );
    const [showCaptcha, setShowCaptcha] = React.useState<boolean>(false);
    const [lookupOngoing, setLookupOngoing] = React.useState<boolean>(false);
    const [loginOngoing, setLoginOngoing] = React.useState<boolean>(false);
    const [signupOngoing, setSignupOngoing] = React.useState<boolean>(false);
    const [signupSecondStep, setSignupSecondStep] = React.useState<boolean>(
        false,
    );
    const [showModal, setShowModal] = React.useState<MODAL_TYPES>(
        MODAL_TYPES.closed,
    );
    const [isModalLoading, setIsModalLoading] = React.useState<boolean>(false);
    // ToS Functions
    const [showToSDialog, setShowToSDialog] = React.useState<boolean>(false);
    const [isToSLoading, setIsToSLoading] = React.useState<boolean>(false);
    const [
        passwordlessCreateAccountFlow,
        setPasswordlessCreateAccountFlow,
    ] = React.useState<boolean>(false);
    // Email Functions
    const [softAccountEmailStatus, setSoftAccountEmailStatus] = React.useState<
        string
    >('');
    const [
        passwordlessEmailStatus,
        setPasswordlessEmailStatus,
    ] = React.useState<string>('');
    const [
        verificationEmailStatus,
        setVerificationEmailStatus,
    ] = React.useState<string>('');
    const [
        resetPasswordEmailStatus,
        setResetPasswordEmailStatus,
    ] = React.useState<string>('');

    const exportedValues: IFrontendContext = {
        methods: {
            setShowForgotPassword,
            setShowCaptcha,
            setLookupOngoing,
            setLoginOngoing,
            setSignupOngoing,
            setSignupSecondStep,
            setShowModal,
            setIsModalLoading,
            setShowToSDialog,
            setIsToSLoading,
            setPasswordlessCreateAccountFlow,
            setSoftAccountEmailStatus,
            setPasswordlessEmailStatus,
            setVerificationEmailStatus,
            setResetPasswordEmailStatus,
        },
        state: {
            showForgotPassword,
            showCaptcha,
            lookupOngoing,
            loginOngoing,
            signupOngoing,
            signupSecondStep,
            showModal,
            isModalLoading,
            showToSDialog,
            isToSLoading,
            passwordlessCreateAccountFlow,
            softAccountEmailStatus,
            passwordlessEmailStatus,
            verificationEmailStatus,
            resetPasswordEmailStatus,
        },
    };

    return (
        <FrontendContext.Provider value={exportedValues}>
            {children}
        </FrontendContext.Provider>
    );
};

export default {
    Context: FrontendContext,
    Consumer: FrontendContext.Consumer,
    Provider: FrontendContextProvider,
};
